import { html, View } from 'rune-ts';
import { ProductBadgeOption, ProductBadgeType, ProductCardData } from '../../../../features/ProductList/type';
import klass from './ProductBadgeList.module.scss';
import { compact, map, pipe, toArray } from '@fxts/core';
import { ProductBadgeBase } from '../ProductBadge/ProductBadgeBase';
import { ProductBadgeListController } from '@services/MarppleShop/renderApp/components/cells/ProductBadgeList/utils/ProductBadgeListController.ts';

export type ProductBadgeListData = Pick<
  ProductCardData,
  | 'is_public'
  | 'ranking'
  | 'sell_start_at'
  | 'sell_end_at'
  | 'quantity'
  | 'order_count'
  | 'is_quantity_public'
  | 'out_of_stock'
  | 'is_token_gate'
  | 'ordered_at'
  | 'is_membership'
  | 'is_trending'
  | 'is_popular'
>;

export interface ProductBadgeListOption extends ProductBadgeOption {
  /**
   * @description 표시할 최대 뱃지 수
   * @default Infinity
   */
  max_length?: number;

  klass?: string;
}

export type ProductBadgeListOverrideOption = {
  [K in ProductBadgeType]?: Partial<Omit<ProductBadgeOption, 'is_mobile'>>;
};

export class ProductBadgeList extends View<Readonly<ProductBadgeListData>> {
  readonly BadgeViews: ProductBadgeBase[];

  private interval_timer_ids: Array<ReturnType<typeof setInterval>> = [];

  private readonly badgeListController: ProductBadgeListController;
  constructor(
    data: Readonly<ProductBadgeListData>,
    private readonly option: Readonly<ProductBadgeListOption>,

    /**
     * 특정 뱃지의 옵션을 오버라이드 하고 싶을 때 사용합니다.
     * `option`의 값에 해당 속성을 override 합니다.
     */
    readonly override_option: Readonly<ProductBadgeListOverrideOption> = {},
  ) {
    super(data, option);
    const { klass, ..._option } = option;
    this.badgeListController = new ProductBadgeListController(data, _option, override_option);
    this.BadgeViews = this.badgeListController.createProductBadges();
  }

  protected override template() {
    return html`<div class="${this.option.klass ?? ''} ${klass.badge_container} ${this.getHiddenKlass()}">
      ${this.BadgeViews}
    </div>`;
  }

  public startBadgesTimer(externalCallback?: () => void): Array<ReturnType<typeof setInterval>> {
    if (!this.option.is_lazy_timer) {
      throw new Error('ProductBadgeList에 is_lazy_timer옵션이 적용되어 있지 않습니다.');
    }

    return (this.interval_timer_ids = pipe(
      this.BadgeViews,
      map((badgeView) => badgeView.startLazyIntervalTimer(externalCallback)),
      compact,
      toArray,
    ));
  }

  public clearIntervalTimers(): void {
    this.interval_timer_ids.forEach((timer_id) => {
      clearInterval(timer_id);
    });

    // flush
    this.interval_timer_ids = [];
  }

  private getHiddenKlass(): string {
    return this.BadgeViews.length ? '' : klass.hidden;
  }
}
